import { Box } from "@mui/material";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { SiteFileImage } from "./media/SiteFileImage";
import type { SiteFileKey } from "./media/siteMedia";
import { revealRef } from "./motion/reveal";
import { SiteTitle } from "./SiteText";

/** 협력사 로고(원래 색) — 이름은 `site.partners.logos` 같은 순서 */
const PARTNER_LOGOS: readonly SiteFileKey[] = [
  "logo/logo-soosungeng.png",
  "logo/logo-taeyoung.png",
  "logo/logo-hyundai.png",
  "logo/logo-eco.png",
  "logo/logo-udae-enp.png",
  "logo/logo-kwater.png",
  "logo/logo-punglim.png",
  "logo/logo-hwalrim.png",
  "logo/logo-eochon-eoang.png",
  "logo/logo-hj.png",
  "logo/logo-libera.png",
];

const STRIP_STYLES = {
  // 위아래 공백을 같게: 앞 구역의 아래 여백이 위 공백이 되므로 위는 0, 아래는 구역 여백만큼
  section: { paddingBlock: `0 ${SITE_VAR.section}` },
  // 홈 첫 장면 바로 아래: 첫 장면 끝과 띠 사이, 띠와 다음 구역 사이를 넉넉히(다음 구역의 위 여백이 더해진다)
  heroSection: {
    position: "relative",
    zIndex: 2,
    paddingBlock: "clamp(120px,14vw,220px) clamp(40px,4.5vw,72px)",
  },
  title: {
    textAlign: "center",
    fontSize: "18px",
    fontWeight: 600,
    letterSpacing: 0,
    color: SITE_COLOR.ink2,
    lineHeight: 1.42,
  },
  marquee: {
    marginTop: "clamp(28px,3.4vw,44px)",
    overflow: "hidden",
    WebkitMaskImage:
      "linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent)",
    maskImage:
      "linear-gradient(90deg,transparent,#000 9%,#000 91%,transparent)",
    "&:hover [data-track], &[data-paused] [data-track]": {
      animationPlayState: "paused",
    },
    [SITE_MEDIA.reducedMotion]: { WebkitMaskImage: "none", maskImage: "none" },
  },
  track: {
    display: "flex",
    alignItems: "center",
    gap: "clamp(52px,6vw,104px)",
    width: "max-content",
    // 로고를 키워 띠가 1.39배 길어진 만큼 시간도 늘려 흐르는 속도는 그대로
    animation: "partnersMarquee 72s linear infinite",
    willChange: "transform",
    "& img": {
      height: "clamp(56px,5.6vw,78px)",
      width: "auto",
      maxWidth: "none",
    },
    "@keyframes partnersMarquee": {
      to: { transform: "translate3d(-50%,0,0)" },
    },
    [SITE_MEDIA.reducedMotion]: {
      animation: "none !important",
      flexWrap: "wrap",
      justifyContent: "center",
      width: "auto",
      rowGap: "32px",
      "& [data-dup]": { display: "none !important" },
    },
  },
} as const;

/**
 * 협력사 띠(marketing-landing): 원래 색 로고가 한 줄로 흐른다. 화면 밖이거나 포인터가 올라가면 멈추고,
 * 움직임 줄이기면 흐르지 않고 여러 줄로 한 번씩만 보인다. 흐름을 끊기지 않게 같은 줄을 한 번 더(장식) 붙인다.
 */
export function PartnersStrip({ inHero = false }: { inHero?: boolean }) {
  const { t } = useTranslation("landing");
  const titleId = useId();
  const marqueeRef = useRef<HTMLDivElement>(null);
  const names = t("site.partners.logos", {
    returnObjects: true,
  }) as readonly string[];

  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) =>
        marquee.toggleAttribute("data-paused", !entry.isIntersecting),
      {
        threshold: 0.15,
      }
    );
    observer.observe(marquee);
    return () => observer.disconnect();
  }, []);

  const logos = (decorative: boolean) =>
    PARTNER_LOGOS.map((logo, index) => (
      <SiteFileImage
        key={logo}
        file={logo}
        alt={decorative ? "" : names[index]}
      />
    ));

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={inHero ? STRIP_STYLES.heroSection : STRIP_STYLES.section}
    >
      <Box sx={SITE_WRAP}>
        <Box
          component="h2"
          id={titleId}
          ref={inHero ? undefined : revealRef}
          data-reveal={inHero ? undefined : "up"}
          sx={STRIP_STYLES.title}
        >
          <SiteTitle text={t("site.partners.title")} />
        </Box>
        <Box
          ref={(element: HTMLDivElement | null) => {
            marqueeRef.current = element;
            return inHero ? undefined : revealRef(element);
          }}
          data-reveal={inHero ? undefined : "fade"}
          sx={STRIP_STYLES.marquee}
        >
          <Box data-track="" sx={STRIP_STYLES.track}>
            {logos(false)}
            <Box
              component="span"
              data-dup=""
              aria-hidden="true"
              sx={{ display: "contents" }}
            >
              {logos(true)}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
